import { useState, useEffect } from 'react';
import { useParams } from 'react-router';
import { Task, ChangeLog } from '../types';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { History, Search, User as UserIcon, Filter } from 'lucide-react';
import { useApp } from '../providers/AppProvider';
import { useOrganizationData } from '../hooks/useOrganizationData';
import { parseDateLike } from '../utils/dateUtils';
import { APP_FIELD_THEME_CLASS, APP_FILTER_LABEL_CLASS } from '../constants/formControls';
import { WIDGET_PANEL_CLASS } from '../constants/widgetTheme';
import { getFieldChangeColors } from '../constants/colorScheme';

type ChangeLogLike = ChangeLog & {
  userId?: string;
  changedBy?: string;
  field?: string;
  fieldName?: string;
  timestamp?: string;
  changedAt?: string;
};

const toTimestamp = (value: unknown): number => parseDateLike(value)?.getTime() ?? 0;

const formatRelative = (value: unknown): string => {
  const date = parseDateLike(value);
  if (!date) return '';
  const diffMs = Date.now() - date.getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const normalizeId = (value: unknown, fallback: string): string => {
  if (typeof value !== 'string') return fallback;
  const normalized = value.trim();
  return normalized.length > 0 ? normalized : fallback;
};

const getLogUserId = (log: ChangeLogLike): string => normalizeId(log.userId ?? log.changedBy, '__unknown_user__');

const CHANGE_TYPE_TO_FIELD: Partial<Record<ChangeLog['changeType'], string>> = {
  status: 'status',
  priority: 'priority',
  assignee: 'assigneeIds',
  estimate: 'estimatedHours',
  targetDate: 'targetCompletionDate',
  startDate: 'startDate',
  targetEndDate: 'targetEndDate',
  cost: 'cost',
  budget: 'budget',
  timeline: 'timeline',
  description: 'description',
  title: 'title',
  complexity: 'complexity',
  phase: 'phase',
  dependencies: 'dependencies',
  category: 'category',
  settings: 'settings',
  other: 'other',
};

const FIELD_ALIASES: Record<string, string> = {
  targetDate: 'targetCompletionDate',
  estimatedManMonths: 'estimatedDays',
};

const normalizeField = (value: unknown): string => {
  const normalized = normalizeId(value, '__unknown_field__');
  return FIELD_ALIASES[normalized] || normalized;
};

const getLogField = (log: ChangeLogLike): string => {
  const explicitField = log.field ?? log.fieldName;
  if (typeof explicitField === 'string' && explicitField.trim().length > 0) {
    return normalizeField(explicitField);
  }

  if (log.changeType) {
    return normalizeField(CHANGE_TYPE_TO_FIELD[log.changeType] || log.changeType);
  }

  return '__unknown_field__';
};

const getLogTimestamp = (log: ChangeLogLike): string => normalizeId(log.timestamp ?? log.changedAt, '');

const getFieldColor = (field: string) => getFieldChangeColors(field);

export default function ProjectChangeLogs() {
  const { projectId } = useParams<{ projectId: string }>();
  const { state } = useApp();
  const {users, tasks, projectIdSet} = useOrganizationData(state);
  const [allChangeLogs, setAllChangeLogs] = useState<Array<ChangeLogLike & { taskId: string; taskTitle: string }>>([]);
  const [filteredLogs, setFilteredLogs] = useState<Array<ChangeLogLike & { taskId: string; taskTitle: string }>>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterUser, setFilterUser] = useState<string>('all');
  const [filterField, setFilterField] = useState<string>('all');

  useEffect(() => {
    // Collect all change logs from all tasks
    const logs: Array<ChangeLogLike & { taskId: string; taskTitle: string }> = [];
    tasks.forEach(task => {
      if (projectIdSet.has(task.projectId) && task.changeLogs && task.changeLogs.length > 0) {
        task.changeLogs.forEach(log => {
          logs.push({
            ...log,
            taskId: task.id,
            taskTitle: task.title,
          });
        });
      }
    });

    // Sort by timestamp descending (newest first)
    logs.sort((a, b) => toTimestamp(getLogTimestamp(b)) - toTimestamp(getLogTimestamp(a)));
    setAllChangeLogs(logs);
    setFilteredLogs(logs);
  }, [projectId, projectIdSet, tasks]);

  useEffect(() => {
    let filtered = [...allChangeLogs];

    // Filter by search term
    if (searchTerm) {
      const loweredSearchTerm = searchTerm.toLowerCase();
      filtered = filtered.filter(log =>
        log.taskTitle.toLowerCase().includes(loweredSearchTerm) ||
        getLogField(log).toLowerCase().includes(loweredSearchTerm) ||
        log.oldValue?.toLowerCase().includes(loweredSearchTerm) ||
        log.newValue?.toLowerCase().includes(loweredSearchTerm)
      );
    }

    // Filter by user
    if (filterUser !== 'all') {
      filtered = filtered.filter(log => getLogUserId(log) === filterUser);
    }

    // Filter by field
    if (filterField !== 'all') {
      filtered = filtered.filter(log => getLogField(log) === filterField);
    }

    setFilteredLogs(filtered);
  }, [searchTerm, filterUser, filterField, allChangeLogs]);

  const getUserName = (userId: string) => {
    const user = users.find((u: { id?: string; name?: string }) => u.id === userId);
    return user?.name || 'Unknown User';
  };

  const getFieldLabel = (field: string) => {
    const fieldLabels: Record<string, string> = {
      status: 'Status',
      priority: 'Priority',
      assigneeIds: 'Assignees',
      targetCompletionDate: 'Target Completion',
      startDate: 'Start Date',
      targetEndDate: 'Target End Date',
      estimatedHours: 'Estimated Hours',
      estimatedDays: 'Estimated Days',
      llmAdjustedHours: 'LLM Adjusted Hours',
      complexity: 'Complexity',
      cost: 'Cost',
      budget: 'Budget',
      timeline: 'Timeline',
      description: 'Description',
      title: 'Title',
      category: 'Category',
      phase: 'Phase',
      dependencies: 'Dependencies',
      settings: 'Settings',
      other: 'Other',
      type: 'Type',
      __unknown_field__: 'Unknown Field',
    };
    if (fieldLabels[field]) {
      return fieldLabels[field];
    }

    return field
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/[_-]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/^./, (char) => char.toUpperCase());
  };

  const uniqueUsers = Array.from(new Set(allChangeLogs.map(log => getLogUserId(log))));
  const uniqueFields = Array.from(new Set(allChangeLogs.map(log => getLogField(log))));

  return (
    <div className="px-4 sm:px-6 lg:px-8 pt-3 lg:pt-4 pb-6 max-w-7xl mx-auto">
      {/* Page Header */}
      <div className="mb-5">
        <div className="flex items-center gap-3 mb-1">
          <History className="w-5 h-5 text-primary" />
          <h1 className="text-2xl font-semibold text-muted-foreground">Task Change Logs</h1>
        </div>
        <p className="text-sm text-muted-foreground ml-8">Complete history of all task changes</p>
      </div>

      {/* Filters */}
      <div className={`${WIDGET_PANEL_CLASS} mb-6 p-4`}>
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end">
          <div className="flex-1 space-y-1.5">
            <span className={APP_FILTER_LABEL_CLASS}>Search</span>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-5 h-5" />
              <Input
                type="text"
                placeholder="Search logs…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={`py-2 pl-10 ${APP_FIELD_THEME_CLASS}`}
              />
            </div>
          </div>

          <div className="w-full space-y-1.5 sm:w-44">
            <span className={APP_FILTER_LABEL_CLASS}>Changed by</span>
            <Select value={filterUser} onValueChange={setFilterUser}>
              <SelectTrigger className={`w-full ${APP_FIELD_THEME_CLASS}`}>
                <div className="flex items-center gap-2 min-w-0">
                  <UserIcon className="w-4 h-4 shrink-0 text-muted-foreground" />
                  <SelectValue placeholder="All users" />
                </div>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All users</SelectItem>
                {uniqueUsers.map((userId, i) => (
                  <SelectItem key={`u-${userId}-${i}`} value={userId}>
                    {getUserName(userId)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="w-full space-y-1.5 sm:w-44">
            <span className={APP_FILTER_LABEL_CLASS}>Field</span>
            <Select value={filterField} onValueChange={setFilterField}>
              <SelectTrigger className={`w-full ${APP_FIELD_THEME_CLASS}`}>
                <div className="flex items-center gap-2 min-w-0">
                  <Filter className="w-4 h-4 shrink-0 text-muted-foreground" />
                  <SelectValue placeholder="All fields" />
                </div>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All fields</SelectItem>
                {uniqueFields.map((field, i) => (
                  <SelectItem key={`f-${field}-${i}`} value={field}>
                    {getFieldLabel(field)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Change Logs List */}
      <div className="space-y-2">
        {filteredLogs.length === 0 ? (
          <div className="py-16 text-center text-muted-foreground">
            <History className="w-10 h-10 mx-auto mb-2 opacity-40" />
            <p className="text-sm">No change logs found</p>
          </div>
        ) : (
          filteredLogs.map((log, index) => {
            const field = getLogField(log);
            const { border, badge } = getFieldColor(field);
            const ts = getLogTimestamp(log);
            return (
              <div
                key={`${log.taskId}-${ts}-${index}`}
                className={`bg-white bg-muted/40 rounded-lg border border-border border-l-4 ${border} px-4 py-3 hover:shadow-sm transition-shadow`}
              >
                {/* Field chip + relative time */}
                <div className="flex items-center justify-between gap-2 mb-1.5">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${badge}`}>
                    {getFieldLabel(field)}
                  </span>
                  <span className="text-xs text-muted-foreground shrink-0">{formatRelative(ts)}</span>
                </div>

                {/* Task title */}
                <p className="text-sm font-medium text-muted-foreground truncate" title={log.taskTitle}>
                  {log.taskTitle}
                </p>

                {/* Author */}
                <p className="text-xs text-muted-foreground mt-0.5 mb-2">
                  by {getUserName(getLogUserId(log))}
                </p>

                {/* Old → New */}
                {(log.oldValue || log.newValue) && (
                  <div className="flex items-center gap-1.5 text-xs">
                    <span
                      className="px-2 py-0.5 bg-destructive/10 text-destructive rounded max-w-[42%] truncate line-through"
                      title={log.oldValue || ''}
                    >
                      {log.oldValue || '—'}
                    </span>
                    <span className="text-muted-foreground">→</span>
                    <span
                      className="px-2 py-0.5 bg-chart-2/10 text-chart-2 rounded font-medium max-w-[42%] truncate"
                      title={log.newValue || ''}
                    >
                      {log.newValue || '—'}
                    </span>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
